import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Typography, Empty, Table, Form, Input, Select, Space, Button, Tag, Pagination, message, Spin, Modal, Tooltip, AutoComplete, Switch } from 'antd';
import { EyeOutlined, TagsOutlined, QuestionCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { fetchUnifiedContainersForLocation, fetchUnifiedContainerDetail, downloadUnifiedContainersForLocation, fetchStorageContainerDetailWithContents, getLocations, previewOutputContainerLabel } from '../../../services/storage';
import { getCurrentUserProjects, getCustomerList } from '../../../services/project';
import { getAllWarehouses } from '../../../services/warehouse';
import { storageLabelService } from '../storage/labels/services/storageLabelService';
import { storageContainerAdapter } from '../storage/labels/adapters/containerAdapter';
import StorageLabelPreview from '../storage/labels/components/StorageLabelPreview';
import { handleLabelError } from '../storage/labels/utils/labelUtils';
import type { StorageContainerLabelData } from '../storage/labels/types/storageLabel';
import dayjs from 'dayjs';
import { usePermissions } from '../../../contexts/PermissionContext';

const { Title, Text } = Typography;

const InventoryLocationPlaceholder: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedContainer, setSelectedContainer] = useState<any | null>(null);
  const [selectedContainerRef, setSelectedContainerRef] = useState<{ id: number; kind: 'storage' | 'output' } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPager, setDetailPager] = useState({
    cPage: 1, cPageSize: 20,
    hPage: 1, hPageSize: 20,
    mPage: 1, mPageSize: 20,
  });
  const [labelPreviewVisible, setLabelPreviewVisible] = useState(false);
  const [labelPreviewData, setLabelPreviewData] = useState<StorageContainerLabelData | undefined>(undefined);
  const [labelPreviewLoading, setLabelPreviewLoading] = useState(false);
  const [outputLabelPreviewVisible, setOutputLabelPreviewVisible] = useState(false);
  const [outputLabelPreviewUrl, setOutputLabelPreviewUrl] = useState<string>('');
  const [projects, setProjects] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [locations, setLocations] = useState<Array<{ id: number; code: string; warehouse_name?: string }>>([]);
  const [customers, setCustomers] = useState<Array<{ id: string; username: string; name?: string; customer_code?: string }>>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const [sorterState, setSorterState] = useState<{ field?: string; order?: 'ascend' | 'descend' }>({});
  const isStaffBackend = typeof window !== 'undefined' && window.location && window.location.pathname.startsWith('/dashboard/staff');
  const { isWarehouseStaff, permissions } = usePermissions();
  const isWHStaff = isWarehouseStaff();
  const isOutputInUseStatus = (statusRaw: any, containerKindRaw: any) => {
    const status = String(statusRaw || '').trim().toLowerCase();
    const containerKind = String(containerKindRaw || '').trim().toLowerCase();
    return containerKind === 'output' && (status === 'active' || status === 'closed');
  };
  const mapStatusDisplayText = (statusRaw: any, statusDisplayRaw: any, containerKindRaw: any) => {
    const status = String(statusRaw || '').trim().toLowerCase();
    const display = String(statusDisplayRaw || '').trim();
    const containerKind = String(containerKindRaw || '').trim().toLowerCase();
    if (isOutputInUseStatus(statusRaw, containerKindRaw)) return '在用';
    if (containerKind === 'storage' && status === 'closed') return '已下架';
    if (display) return display;
    if (status === 'active') return '在用';
    if (status === 'closed') return '关闭';
    if (status === 'consumed') return '已消耗';
    if (status === 'lost') return '丢失';
    return status || '-';
  };
  const selectedContainerKind = Form.useWatch('container_kind', form);
  const onlyActiveContainers = Form.useWatch('only_active_containers', form);
  const statusFilterDisabled = !selectedContainerKind || !!onlyActiveContainers;
  const getInUseStatusByContainerKind = (containerKindRaw: any) => {
    const containerKind = String(containerKindRaw || '').trim().toLowerCase();
    if (containerKind === 'output') return 'in_use';
    if (containerKind === 'storage') return 'active';
    return undefined;
  };
  useEffect(() => {
    if (!onlyActiveContainers) return;
    const kind = form.getFieldValue('container_kind');
    form.setFieldsValue({ status: getInUseStatusByContainerKind(kind) });
  }, [onlyActiveContainers, selectedContainerKind]);
  const openIocHierarchyOverview = async (row: any) => {
    try {
      const id = Number(row?.id || 0);
      if (!(id > 0)) return;
      const resp = await fetchUnifiedContainerDetail('output', id, { c_page_size: 1, h_page_size: 1, m_page_size: 1 });
      const detail = resp?.data || {};
      const container = detail?.container || {};
      const hierarchy = (container?.hierarchy || {}) as any;
      const role = String(hierarchy?.role || 'standalone').trim().toLowerCase();
      const parent = hierarchy?.parent_container || null;
      const children = Array.isArray(hierarchy?.child_containers) ? hierarchy.child_containers : [];
      const code = String(container?.display_code || container?.system_code || row?.display_code || '-').trim() || '-';
      const roleText = role === 'child' ? '子容器' : role === 'parent' ? '父容器' : '独立容器';
      const fullInfoText = [
        `容器系统编码: ${code}`,
        `当前容器层级: ${roleText}`,
        parent
          ? `所属父容器: ${String(parent?.code || '-')} | 状态: ${String(parent?.status_display || '-')} | 仓位: ${String(parent?.current_location_display || '-')}`
          : '所属父容器: -',
        ...(children.length > 0
          ? ['子容器列表:', ...children.map((ch: any, idx: number) => `${idx + 1}. ${String(ch?.code || '-')} | 状态: ${String(ch?.status_display || '-')} | 仓位: ${String(ch?.current_location_display || '-')}`)]
          : ['子容器列表: -']),
      ].join('\n');
      Modal.info({
        title: (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 12, paddingRight: 24 }}>
            <span>容器层级概览：{code}</span>
            <Button
              type="link"
              size="small"
              onClick={() => { void copyText(fullInfoText, '已复制完整信息'); }}
            >
              复制信息
            </Button>
          </div>
        ),
        width: 760,
        centered: true,
        content: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              当前容器层级：
              {` ${roleText}`}
            </div>
            {parent ? (
              <div>
                <div style={{ color: '#595959', marginBottom: 6 }}>所属父容器</div>
                <Space size={8} wrap>
                  <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9', fontWeight: 700 }}>
                    {String(parent?.code || '-')}
                  </Tag>
                  <Tag>{String(parent?.status_display || '-')}</Tag>
                  <Tag>{String(parent?.current_location_display || '-')}</Tag>
                </Space>
              </div>
            ) : null}
            {children.length > 0 ? (
              <div>
                <div style={{ color: '#595959', marginBottom: 6 }}>子容器列表</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {children.map((ch: any, idx: number) => (
                    <Space key={`quick-hier-child-${String(ch?.id || idx)}`} size={6} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #d9d9d9', background: '#fafafa' }}>
                      <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#fff1f0', borderColor: '#ffa39e', color: '#cf1322', fontWeight: 700 }}>
                        {String(ch?.code || '-')}
                      </Tag>
                      <Tag>{String(ch?.status_display || '-')}</Tag>
                    </Space>
                  ))}
                </div>
              </div>
            ) : null}
            {role === 'standalone' && !parent && children.length === 0 ? (
              <div style={{ color: '#8c8c8c' }}>该容器暂无父子层级关系。</div>
            ) : null}
          </div>
        ),
      });
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '加载容器层级概览失败');
    }
  };
  const renderIocHierarchyLevelTag = (row: any) => {
    const isChild = !!row?.is_child_container;
    const isParent = !!row?.is_parent_container;
    const clickableProps = {
      role: 'button' as const,
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        void openIocHierarchyOverview(row);
      },
      style: { cursor: 'pointer' as const, userSelect: 'none' as const },
      title: '点击查看层级概览',
    };
    if (isChild) {
      return (
        <Tag {...clickableProps} style={{ marginInlineEnd: 0, borderRadius: 999, background: '#fff1f0', borderColor: '#ffa39e', color: '#cf1322', fontWeight: 700 }}>
          子
        </Tag>
      );
    }
    if (isParent) {
      return (
        <Tag {...clickableProps} style={{ marginInlineEnd: 0, borderRadius: 999, background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9', fontWeight: 700 }}>
          父
        </Tag>
      );
    }
    return (
      <Tag {...clickableProps} style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontWeight: 700 }}>
        独立
      </Tag>
    );
  };
  const iocHierarchyRoleOptions = useMemo(() => ([
    { value: 'child', label: '子容器' },
    { value: 'parent', label: '父容器' },
    { value: 'standalone', label: '独立容器' },
  ]), []);
  const statusOptions = useMemo(() => {
    if (selectedContainerKind === 'storage') {
      return [
        { value: 'active', label: '在用' },
        { value: 'closed', label: '已下架' },
        { value: 'lost', label: '丢失' },
      ];
    }
    if (selectedContainerKind === 'output') {
      return [
        { value: 'in_use', label: '在用' },
        { value: 'consumed', label: '已消耗' },
        { value: 'cancelled', label: '已作废' },
        { value: 'lost', label: '丢失' },
      ];
    }
    return [];
  }, [selectedContainerKind]);

  const getTabId = () => {
    try { return new URLSearchParams(window.location.search).get('tabId') || ''; } catch { return ''; }
  };
  const getStorageKey = () => {
    const tabId = getTabId();
    return `location_table_state_${tabId || window.location.pathname}`;
  };

  const saveStateToStorage = (override?: Partial<{ page: number; pageSize: number; sorter?: { field?: string; order?: 'ascend' | 'descend' } }>) => {
    try {
      const raw = form.getFieldsValue();
      const saved = {
        form: raw,
        page: override?.page ?? page,
        pageSize: override?.pageSize ?? pageSize,
        sorter: override?.sorter ?? sorterState
      };
      localStorage.setItem(getStorageKey(), JSON.stringify(saved));
    } catch {}
  };

  const loadStateFromStorage = () => {
    try {
      const str = localStorage.getItem(getStorageKey());
      if (!str) return null;
      return JSON.parse(str);
    } catch { return null; }
  };

  const triggerSearch = (delay = 400) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      saveStateToStorage({ page: 1 });
      fetchList({ page: 1 });
    }, delay);
  };

  const handleInputChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = (e?.target?.value ?? '').toString();
    form.setFieldsValue({ [name]: value });
    triggerSearch();
  };

  const copyText = async (text: string, successMsg = '已复制') => {
    const val = String(text || '').trim();
    if (!val || val === '-') return;
    try {
      await navigator.clipboard.writeText(val);
      message.success(successMsg);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = val;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        message.success(successMsg);
      } catch {
        message.error('复制失败，请手动复制');
      }
    }
  };

  const loadContainerDetail = async (
    target: { id: number; kind: 'storage' | 'output' },
    pagerOverride?: Partial<typeof detailPager>,
  ) => {
    setDetailLoading(true);
    try {
      const merged = { ...detailPager, ...(pagerOverride || {}) };
      const resp = await fetchUnifiedContainerDetail(target.kind, target.id, {
        c_page: merged.cPage,
        c_page_size: merged.cPageSize,
        h_page: merged.hPage,
        h_page_size: merged.hPageSize,
        m_page: merged.mPage,
        m_page_size: merged.mPageSize,
      });
      setDetailPager(merged);
      setSelectedContainer(resp.data);
      setSelectedContainerRef(target);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchList = async (override?: Partial<{ page: number; pageSize: number; ordering?: string }>) => {
    setLoading(true);
    try {
      const raw = form.getFieldsValue();
      let values = {
        ...raw,
        warehouse_code: (raw.warehouse_code ?? '').toString().trim() || undefined,
        project_code: (raw.project_code ?? '').toString().trim() || undefined,
        system_code: (raw.system_code ?? '').toString().trim() || undefined,
        current_location_code: (raw.current_location_code ?? '').toString().trim() || undefined,
        inventory_unit_code: (raw.inventory_unit_code ?? '').toString().trim() || undefined,
        sorting_code: (raw.sorting_code ?? '').toString().trim() || undefined,
        ioc_hierarchy_role: (raw.ioc_hierarchy_role ?? '').toString().trim() || undefined,
      } as any;
      const effectiveStatus = values.only_active_containers
        ? 'in_use'
        : values.status;

      // 将多选项目（id 数组）转换为后端可接受的逗号分隔字符串参数 project
      if (Array.isArray(raw.project) && raw.project.length > 0) {
        values.project = raw.project.join(',');
      } else {
        values.project = undefined;
      }
      // 计算排序参数（仅允许后端支持的字段）
      const ordering = override?.ordering;

      const resp = await fetchUnifiedContainersForLocation({
        page: override?.page || page,
        page_size: override?.pageSize || pageSize,
        customer_id: values.customer_id,
        container_kind: values.container_kind,
        content_kind: values.content_kind,
        putaway_status: values.putaway_status,
        warehouse_code: values.warehouse_code?.trim(),
        project: values.project,
        system_code: values.system_code?.trim(),
        inventory_unit_code: values.inventory_unit_code,
        sorting_code: values.sorting_code,
        container_type: values.container_type,
        status: effectiveStatus,
        ioc_hierarchy_role: values.container_kind === 'output' ? values.ioc_hierarchy_role : undefined,
        current_location_code: values.current_location_code?.trim(),
        exclude_active_reservations: false,
        ordering,
      });
      setData(resp.data.results || []);
      setTotal(resp.data.count || 0);
      if (override?.page) setPage(override.page);
      if (override?.pageSize) setPageSize(override.pageSize);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 优先从本地存储恢复；否则设置默认值
    const saved = loadStateFromStorage();
    if (saved && saved.form) {
      // 历史版本默认写入了 status=active，会把 IOC(常见 closed/shipped_out) 全部筛掉。
      // 统一容器模式下，将这类“旧默认值”自动清空，避免误过滤。
      if ((saved.form?.status || '') === 'active' && !saved.form?.container_kind) {
        saved.form.status = undefined;
      }
      // 指令产出容器：兼容历史值（active/closed）为新的聚合值 in_use
      if (saved.form?.container_kind === 'output' && ['active', 'closed'].includes(String(saved.form?.status || ''))) {
        saved.form.status = 'in_use';
      }
      // 仅当来源=指令产出容器时，才保留 IOC 层级筛选
      if (saved.form?.container_kind !== 'output' && saved.form?.ioc_hierarchy_role) {
        saved.form.ioc_hierarchy_role = undefined;
      }
      if (saved.form?.container_kind === 'output' && saved.form?.ioc_hierarchy_role) {
        const allowedRoles = new Set(['child', 'parent', 'standalone']);
        if (!allowedRoles.has(String(saved.form.ioc_hierarchy_role || '').trim())) {
          saved.form.ioc_hierarchy_role = undefined;
        }
      }
      // 未选择容器来源时，状态筛选不可用；清空遗留状态值，避免产生隐藏过滤。
      if (!saved.form?.container_kind && saved.form?.status) {
        saved.form.status = undefined;
      }
      // 新增筛选项：默认仅显示在用容器（兼容历史本地缓存）
      if (typeof saved.form?.only_active_containers !== 'boolean') {
        saved.form.only_active_containers = true;
      }
      try { form.setFieldsValue(saved.form); } catch {}
      setSorterState(saved.sorter || {});
      if (saved.page || saved.pageSize || (saved.sorter && saved.sorter.field)) {
        const ord = saved.sorter && saved.sorter.field ? (saved.sorter.order === 'descend' ? `-${saved.sorter.field}` : saved.sorter.field) : undefined;
        fetchList({ page: saved.page || 1, pageSize: saved.pageSize || pageSize, ordering: ord });
      } else {
        fetchList();
      }
    } else {
      // 默认仅显示在用容器
      try { form.setFieldsValue({ only_active_containers: true }); } catch {}
      fetchList();
    }
    // 载入下拉数据
    (async () => {
      try {
        setLoadingProjects(true);
        const ps = await getCurrentUserProjects();
        setProjects(ps || []);
      } catch (e) {
        message.error('加载项目列表失败');
      } finally { setLoadingProjects(false); }

      try {
        setLoadingWarehouses(true);
        const ws = await getAllWarehouses();
        const assignedWarehouseIds = (permissions?.assigned_warehouse_ids || []) as number[];
        let allowed = ws || [];
        if (isWHStaff && Array.isArray(assignedWarehouseIds) && assignedWarehouseIds.length > 0) {
          const idSet = new Set(assignedWarehouseIds);
          allowed = (ws || []).filter((w: any) => idSet.has(w.id));
        }
        setWarehouses(allowed);
        // 员工：若未选择仓库，默认选中第一个被分配仓库，并加载其仓位
        const currentWarehouseCode = (form.getFieldValue('warehouse_code') || '').toString();
        if (isWHStaff && !currentWarehouseCode && allowed && allowed.length > 0) {
          try { form.setFieldsValue({ warehouse_code: allowed[0].code }); } catch {}
          try {
            const ls = await getLocations({ warehouse_code: allowed[0].code });
            setLocations(ls || []);
          } catch {}
        }
      } catch (e) {
        message.error('加载仓库列表失败');
      } finally { setLoadingWarehouses(false); }
      // 初始化仓位列表：如员工仅显示分配仓库的仓位
      try {
        const currentWarehouseCode = (form.getFieldValue('warehouse_code') || '').toString();
        const ls = await getLocations(currentWarehouseCode ? { warehouse_code: currentWarehouseCode } : undefined);
        setLocations(ls || []);
      } catch {}

      try {
        setLoadingCustomers(true);
        const cs = await getCustomerList({ onlyMain: true });
        setCustomers(cs || []);
      } catch (e) {
        message.error('加载客户列表失败');
      } finally { setLoadingCustomers(false); }
    })();
  }, []);

  const columns = useMemo(() => ([
    {
      title: '来源',
      dataIndex: 'container_kind_display',
      key: 'container_kind_display',
      width: 170,
      render: (v: string, r: any) => {
        const isOutput = String(r?.container_kind || '').trim().toLowerCase() === 'output';
        if (!isOutput) {
          return <Tag color="blue">{v || '-'}</Tag>;
        }
        const contentKind = String(r?.output_content_kind || '').trim().toLowerCase();
        if (contentKind === 'normal') {
          return (
            <Tag
              style={{
                marginInlineEnd: 0,
                borderRadius: 999,
                background: '#f6ffed',
                borderColor: '#b7eb8f',
                color: '#389e0d',
                fontWeight: 700,
              }}
            >
              指令产出容器-正常
            </Tag>
          );
        }
        if (contentKind === 'exception') {
          return (
            <Tag
              style={{
                marginInlineEnd: 0,
                borderRadius: 999,
                background: '#fff0f6',
                borderColor: '#ffadd2',
                color: '#c41d7f',
                fontWeight: 700,
              }}
            >
              指令产出容器-异常
            </Tag>
          );
        }
        return <Tag color="purple">{v || '指令产出容器'}</Tag>;
      },
    },
    {
      title: '系统编码',
      dataIndex: 'display_code',
      key: 'display_code',
      width: 260,
      render: (v: string, r: any) => {
        const code = String(v || '').trim();
        if (!code) return '-';
        const isOutput = String(r?.container_kind || '').trim().toLowerCase() === 'output';
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Text code copyable={{ text: code }}>{code}</Text>
            {isOutput ? renderIocHierarchyLevelTag(r) : null}
          </span>
        );
      },
    },
    { title: '容器类型', dataIndex: 'container_type_display', key: 'container_type_display', width: 120, render: (v: string) => <Tag color="blue">{v}</Tag> },
    {
      title: '所含内容物',
      dataIndex: 'content_kind_display',
      key: 'content_kind_display',
      width: 180,
      render: (v: string, r: any) => {
        const kind = String(r?.content_kind || '').trim().toLowerCase();
        const kindsRaw = Number(
          r?.output_contents_kinds
          ?? r?.contents_kinds
          ?? r?.content_kinds
          ?? NaN,
        );
        const totalRaw = Number(
          r?.output_contents_total_qty
          ?? r?.contents_total_qty
          ?? r?.total_qty
          ?? NaN,
        );
        const hasKinds = Number.isFinite(kindsRaw) && kindsRaw >= 0;
        const hasTotal = Number.isFinite(totalRaw) && totalRaw >= 0;
        if (kind === 'single') {
          const txt = hasTotal ? `单装${Math.trunc(totalRaw)}件` : (v || '单装');
          return (
            <Tag
              style={{
                marginInlineEnd: 0,
                borderRadius: 999,
                background: '#f6ffed',
                borderColor: '#b7eb8f',
                color: '#389e0d',
                fontWeight: 700,
              }}
            >
              {txt}
            </Tag>
          );
        }
        if (kind === 'mixed') {
          const txt = (hasKinds && hasTotal)
            ? `混装 ${Math.trunc(kindsRaw)}款${Math.trunc(totalRaw)}件`
            : (v || '混装');
          return (
            <Tag
              style={{
                marginInlineEnd: 0,
                borderRadius: 999,
                background: '#fff0f6',
                borderColor: '#ffadd2',
                color: '#c41d7f',
                fontWeight: 700,
              }}
            >
              {txt}
            </Tag>
          );
        }
        return v || '-';
      },
    },
    { title: '所属客户', dataIndex: 'customer_username', key: 'customer_username', width: 160, render: (v: string) => v || '-' },
    { title: '项目', key: 'project', width: 180, render: (_: any, r: any) => <span>{r.project_code} - {r.project_name}</span> },
    { title: '所属仓库', dataIndex: 'warehouse_name', key: 'warehouse_name', width: 140 },
    { title: '当前/最后仓位', dataIndex: 'current_location_display', key: 'current_location_code', sorter: true, width: 140, render: (v: string) => v ? <Tag>{v}</Tag> : <Text type="secondary">-</Text> },
    {
      title: '状态',
      dataIndex: 'status_display',
      key: 'status_display',
      width: 100,
      render: (v: string, r: any) => {
        const raw = String(r?.status || '').toLowerCase();
        const kind = String(r?.container_kind || '').toLowerCase();
        const txt = mapStatusDisplayText(r?.status, v, r?.container_kind);
        const isActive = raw === 'active' || txt === '在用' || txt === '生效' || txt.toUpperCase() === 'ACTIVE';
        const isConsumed = raw === 'consumed' || txt === '已消耗';
        const color = isActive ? 'green' : (isConsumed ? 'orange' : 'default');
        const tagNode = <Tag color={color}>{txt || '-'}</Tag>;
        if (kind === 'output' && isConsumed) {
          const occupiedBy = String(r?.occupying_instruction_code || '').trim() || '-';
          return (
            <Tooltip title={`被占用指令编码：${occupiedBy}`}>
              {tagNode}
            </Tooltip>
          );
        }
        return tagNode;
      },
    },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', sorter: true, width: 160, render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm') },
    { title: '操作', key: 'action', fixed: 'right' as const, width: 240, render: (_: any, rec: any) => (
      <Space size="small">
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          onClick={async () => {
            const target = { id: Number(rec.id), kind: String(rec.container_kind) as ('storage' | 'output') };
            await loadContainerDetail(target, {
              cPage: 1, cPageSize: 20,
              hPage: 1, hPageSize: 20,
              mPage: 1, mPageSize: 20,
            });
          }}
        >查看</Button>
        {rec.container_kind === 'storage' && (
          <Button
            type="text"
            size="small"
            icon={<TagsOutlined />}
            onClick={async () => {
              try {
                setLabelPreviewLoading(true);
                // 仅仓储容器支持当前标签模板
                let contentItems: Array<{ name: string; quantity: number; type?: string }> = [];
                try {
                  const resp = await fetchStorageContainerDetailWithContents(rec.id, { c_page_size: 200 });
                  const items = Array.isArray(resp?.data?.contents?.results) ? resp.data.contents.results : [];
                  contentItems = items.map((it: any) => ({ name: it.inventory_unit_code, quantity: Number(it.quantity || 0) }));
                } catch {}
                const contentSummary = storageContainerAdapter.createContentSummary(contentItems);
                if (contentItems.length > 0) {
                  (contentSummary as any).items = contentItems.map(ci => ({ code: ci.name, quantity: ci.quantity }));
                }
                const labelData = storageContainerAdapter.transform(rec, {
                  contentSummary,
                  printOptions: { copies: 1, directPrint: false },
                  templateId: storageContainerAdapter.getDefaultTemplateId(rec.container_type)
                });
                setLabelPreviewData(labelData);
                setLabelPreviewVisible(true);
              } catch (error) {
                const info: any = handleLabelError ? handleLabelError(error, '预览') : { details: (error as any)?.message || '未知错误' };
                message.error(`生成标签预览失败：${info.details}`);
              } finally {
                setLabelPreviewLoading(false);
              }
            }}
          >打印标签</Button>
        )}
        {rec.container_kind === 'output' && (
          <Button
            type="text"
            size="small"
            icon={<TagsOutlined />}
            onClick={async () => {
              try {
                setLabelPreviewLoading(true);
                const detailResp = await fetchUnifiedContainerDetail('output', Number(rec?.id || 0), { c_page_size: 500 });
                const detail = detailResp?.data || {};
                const container = detail?.container || {};
                const contentRows = Array.isArray(detail?.contents?.results) ? detail.contents.results : [];
                const details = contentRows
                  .map((it: any) => ({
                    new_code: String(it?.inventory_unit_code || '').trim(),
                    qty: Number(it?.quantity || 0) || 0,
                  }))
                  .filter((x: any) => x.new_code && x.qty > 0)
                  .sort((a: any, b: any) => (b.qty || 0) - (a.qty || 0));
                const totalQty = details.reduce((sum: number, d: any) => sum + Number(d?.qty || 0), 0);
                const newKinds = details.length;
                const projectSummary = `${String(container?.project_code || rec?.project_code || '').trim()}${String(container?.project_name || rec?.project_name || '').trim() ? ` ${String(container?.project_name || rec?.project_name || '').trim()}` : ''}`.trim();
                const l = Number(container?.length_cm ?? rec?.length_cm);
                const w = Number(container?.width_cm ?? rec?.width_cm);
                const h = Number(container?.height_cm ?? rec?.height_cm);
                const g = Number(container?.weight_g ?? rec?.weight_g);
                const toIn = (cm: number) => (Number.isFinite(cm) && cm > 0 ? Number((cm / 2.54).toFixed(2)) : '-');
                const toKg = (gg: number) => (Number.isFinite(gg) && gg > 0 ? Number((gg / 1000).toFixed(3)) : '-');
                const toLb = (gg: number) => (Number.isFinite(gg) && gg > 0 ? Number((gg / 453.59237).toFixed(3)) : '-');
                const outputKind = String(container?.content_kind || rec?.output_content_kind || '').trim().toLowerCase();
                const payload: any = {
                  container_code: String(container?.display_code || rec?.display_code || '').trim(),
                  instruction_id: Number(container?.instruction_id || rec?.instruction_id || 0) || undefined,
                  instruction_code: String(container?.instruction_code || rec?.instruction_code || '').trim() || undefined,
                  project_summary: projectSummary || undefined,
                  container_type_display: String(container?.container_type_display || rec?.container_type_display || '').trim() || undefined,
                  created_at: (container?.created_at || rec?.created_at) ? dayjs(container?.created_at || rec?.created_at).format('YYYY-MM-DD HH:mm:ss') : undefined,
                  label_variant: outputKind === 'exception' ? 'exception' : 'normal',
                  dims_cm: {
                    l: Number.isFinite(l) && l > 0 ? Math.round(l) : '-',
                    w: Number.isFinite(w) && w > 0 ? Math.round(w) : '-',
                    h: Number.isFinite(h) && h > 0 ? Math.round(h) : '-',
                  },
                  dims_in: { l: toIn(l), w: toIn(w), h: toIn(h) },
                  weight: { kg: toKg(g), lb: toLb(g) },
                  new_kinds: newKinds,
                  total_qty: totalQty,
                  details: details.slice(0, 50),
                };
                const resp = await previewOutputContainerLabel(payload);
                const blob = (resp as any)?.data as Blob;
                if (outputLabelPreviewUrl) {
                  try { URL.revokeObjectURL(outputLabelPreviewUrl); } catch {}
                }
                const url = URL.createObjectURL(blob);
                setOutputLabelPreviewUrl(url);
                setOutputLabelPreviewVisible(true);
              } catch (error: any) {
                message.error(error?.response?.data?.error || error?.response?.data?.detail || error?.message || '生成IOC标签预览失败');
              } finally {
                setLabelPreviewLoading(false);
              }
            }}
          >打印标签</Button>
        )}
      </Space>
    )},
  ]), [page, pageSize, outputLabelPreviewUrl]);

  return (
    <div className="p-6">
      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Title level={4} style={{ margin: 0 }}>仓位表</Title>
          <Space>
            <Button size="small" type="primary" onClick={async () => {
              // 打印当前筛选结果（不分页），页面尺寸4x6英寸，仅显示三列，按仓位排序
              const raw = form.getFieldsValue();
              const values = {
                ...raw,
                warehouse_code: (raw.warehouse_code ?? '').toString().trim() || undefined,
                project: Array.isArray(raw.project) && raw.project.length > 0 ? raw.project.join(',') : undefined,
                system_code: (raw.system_code ?? '').toString().trim() || undefined,
                current_location_code: (raw.current_location_code ?? '').toString().trim() || undefined,
                inventory_unit_code: (raw.inventory_unit_code ?? '').toString().trim() || undefined,
                status: (raw.status ?? '').toString().trim() || undefined,
                ioc_hierarchy_role: (raw.ioc_hierarchy_role ?? '').toString().trim() || undefined,
              } as any;
              const effectiveStatus = values.only_active_containers
                ? 'in_use'
                : values.status;

              const resp = await fetchUnifiedContainersForLocation({
                page: 1,
                page_size: 1000,
                customer_id: values.customer_id,
                container_kind: values.container_kind,
                content_kind: values.content_kind,
                warehouse_code: values.warehouse_code,
                project: values.project,
                system_code: values.system_code,
                inventory_unit_code: values.inventory_unit_code,
                sorting_code: values.sorting_code,
                container_type: values.container_type,
                status: effectiveStatus,
                ioc_hierarchy_role: values.container_kind === 'output' ? values.ioc_hierarchy_role : undefined,
                current_location_code: values.current_location_code,
                exclude_active_reservations: false,
              });
              const rows = Array.isArray(resp.data?.results) ? resp.data.results : [];
              // 按当前仓位排序（为空排后）
              rows.sort((a: any, b: any) => {
                const av = a.current_location_code || '\uFFFF';
                const bv = b.current_location_code || '\uFFFF';
                return String(av).localeCompare(String(bv), 'zh');
              });

              const now = new Date().toLocaleString();
              const rowsPerPage = 20;
              const totalPages = Math.ceil(rows.length / rowsPerPage) || 1;
              const pagesHtml = Array.from({ length: totalPages }).map((_, i) => {
                const startIdx = i * rowsPerPage;
                const endIdx = Math.min(startIdx + rowsPerPage, rows.length);
                const displayStart = startIdx + 1;
                const displayEnd = endIdx;
                const pageRows = rows.slice(startIdx, endIdx).map((r: any) => {
                  const project = `${r.project_code || ''}`;
                  const loc = r.current_location_code || '';
                  return `<tr><td>${r.container_kind_display || ''}</td><td class="code">${r.display_code || ''}</td><td>${project}</td><td class="code">${loc}</td></tr>`;
                }).join('');
                return `
      <div class="page">
        <div class="header">
          <h1>Location List (${rows.length})</h1>
          <div class="time">${now}</div>
        </div>
        <div class="content">
          <table>
            <thead><tr><th>Kind</th><th>System Code</th><th>Project</th><th>Location</th><th>Picked</th></tr></thead>
            <tbody>
              ${pageRows}
            </tbody>
          </table>
        </div>
        <div class="footer">
          <div>${i + 1}/${totalPages}</div>
          <div>Items ${displayStart}-${displayEnd} of ${rows.length}</div>
        </div>
      </div>`;
              }).join('');

              const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8" />
<title>打印仓位</title>
<style>
  @page { size: 4in 6in; margin: 0.25in; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'PingFang SC', 'Microsoft YaHei', sans-serif; }
  h1 { font-size: 15px; margin: 0; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; table-layout: fixed; }
  th, td { border: 1px solid #000; padding: 4px 6px; word-break: break-all; line-height: 1.35; }
  th { background: #f0f0f0; text-align: left; }
  tbody td { font-weight: 700; }
  .code { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
  .page { page-break-after: always; display: flex; flex-direction: column; min-height: calc(6in - 0.5in); }
  .header { margin-bottom: 8px; }
  .header .time { font-size: 12px; margin-top: 4px; }
  .content { flex: 1 1 auto; }
  .footer { display: flex; justify-content: space-between; align-items: center; margin-top: 4px; font-size: 12px; }
  td.picked, th.picked { text-align: center; width: 48px; }
  @media print { .page:last-child { page-break-after: auto; } }
</style>
</head>
<body>
  ${pagesHtml}
</body></html>`;
              try {
                const iframe = document.createElement('iframe');
                iframe.style.position = 'fixed';
                iframe.style.right = '0';
                iframe.style.bottom = '0';
                iframe.style.width = '0';
                iframe.style.height = '0';
                iframe.style.border = '0';
                iframe.style.visibility = 'hidden';
                document.body.appendChild(iframe);

                // 在写入内容前绑定 onload，确保捕获加载完成事件
                iframe.onload = () => {
                  // 等待一帧，确保样式与布局完成后再触发打印
                  requestAnimationFrame(() => {
                    try {
                      iframe.contentWindow?.focus();
                      iframe.contentWindow?.print();
                    } finally {
                      // 打印后延迟移除 iframe，避免打断首次打印
                      setTimeout(() => {
                        try { document.body.removeChild(iframe); } catch {}
                      }, 800);
                    }
                  });
                };

                const idoc = (iframe.contentWindow?.document || iframe.contentDocument) as Document;
                idoc.open();
                // 追加“Picked”列的单元格内容（与表头一致，空白可手写）
                const withPicked = html.replace(
                  /<tbody>\s*([\s\S]*?)\s*<\/tbody>/g,
                  (m) => m.replace(/<tr>([\s\S]*?)<\/tr>/g, (row) => row.replace(/<\/tr>/, '<td class="picked"></td></tr>'))
                ).replace('<th>Location</th>', '<th>Location<\/th>').replace('<th>Location</th>', '<th>Location</th>');
                idoc.write(withPicked);
                idoc.close();
              } catch (e) {
                message.error('打印失败，请重试');
              }
            }}>{`打印仓位（${Math.min(total, 1000)}）`}</Button>
            <Button size="small" onClick={async () => {
              try {
                const raw = form.getFieldsValue();
                const values = {
                  ...raw,
                  warehouse_code: (raw.warehouse_code ?? '').toString().trim() || undefined,
                  project: Array.isArray(raw.project) && raw.project.length > 0 ? raw.project.join(',') : undefined,
                  system_code: (raw.system_code ?? '').toString().trim() || undefined,
                  current_location_code: (raw.current_location_code ?? '').toString().trim() || undefined,
                  inventory_unit_code: (raw.inventory_unit_code ?? '').toString().trim() || undefined,
                  status: (raw.status ?? '').toString().trim() || undefined,
                  ioc_hierarchy_role: (raw.ioc_hierarchy_role ?? '').toString().trim() || undefined,
                } as any;
                const effectiveStatus = values.only_active_containers
                  ? 'in_use'
                  : values.status;

                // 使用与表格相同的排序（若有）
                const sorterEl = document.querySelector('.ant-table');
                const ordering = undefined; // 简化：如需完善可存储上次 ordering

          const resp = await downloadUnifiedContainersForLocation({
                  page: 1,
                  page_size: Math.min(total || 1000, 1000),
                  customer_id: values.customer_id,
                  container_kind: values.container_kind,
                  content_kind: values.content_kind,
                  putaway_status: values.putaway_status,
                  warehouse_code: values.warehouse_code,
                  project: values.project,
                  system_code: values.system_code,
                  inventory_unit_code: values.inventory_unit_code,
                  container_type: values.container_type,
                  status: effectiveStatus,
                  ioc_hierarchy_role: values.container_kind === 'output' ? values.ioc_hierarchy_role : undefined,
                  current_location_code: values.current_location_code,
                  exclude_active_reservations: false,
                  ordering,
                });
                const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
                const disposition = (resp.headers && (resp.headers['content-disposition'] || resp.headers['Content-Disposition'])) as string | undefined;
                let filename = '';
                if (disposition) {
                  const match = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition);
                  if (match && match[1]) filename = match[1];
                }
                if (!filename) {
                  const today = new Date();
                  const y = today.getFullYear();
                  const m = String(today.getMonth() + 1).padStart(2, '0');
                  const d = String(today.getDate()).padStart(2, '0');
                  filename = `storage_containers_${y}${m}${d}.csv`;
                }
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
              } catch (e) {
                message.error('下载失败，请重试');
              }
            }}>{`批量下载（${total}）`}</Button>
          </Space>
        </div>
        <Form form={form} initialValues={{ only_active_containers: true }} layout="inline" style={{ marginTop: 12 }} onFinish={() => { saveStateToStorage({ page: 1 }); fetchList({ page: 1 }); }}>
          {/* 第一行：容器来源 状态 所属客户 所属项目 所含库存单元 容器系统编码 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
            <Form.Item name="container_kind" label="容器来源">
              <Select
                allowClear
                style={{ width: 140 }}
                placeholder="全部"
                onChange={(val) => {
                  // 切换来源时清空状态，避免跨来源遗留无效状态值
                  form.setFieldsValue({
                    status: onlyActiveContainers ? getInUseStatusByContainerKind(val) : undefined,
                    ioc_hierarchy_role: undefined,
                  });
                  saveStateToStorage({ page: 1 });
                  fetchList({ page: 1 });
                }}
                options={[
                  { value: 'storage', label: '仓储容器' },
                  { value: 'output', label: '指令产出容器' },
                ]}
              />
            </Form.Item>
            <Form.Item label="状态">
              <Tooltip title={statusFilterDisabled ? '只有选定容器来源并关闭“仅显示在用容器”开关后才能筛选状态' : ''}>
                <Form.Item name="status" noStyle>
                  <Select
                    allowClear
                    disabled={statusFilterDisabled}
                    placeholder={onlyActiveContainers ? '在用' : (selectedContainerKind ? '不限' : '请先选择容器来源')}
                    style={{ width: 180 }}
                    onChange={() => { saveStateToStorage({ page: 1 }); fetchList({ page: 1 }); }}
                    options={statusOptions}
                  />
                </Form.Item>
              </Tooltip>
            </Form.Item>
            {selectedContainerKind === 'output' && (
              <Form.Item name="ioc_hierarchy_role" label="容器层级">
                <Select
                  allowClear
                  placeholder="不限"
                  style={{ width: 170 }}
                  onChange={() => { saveStateToStorage({ page: 1 }); fetchList({ page: 1 }); }}
                  options={iocHierarchyRoleOptions}
                />
              </Form.Item>
            )}
            {!isStaffBackend && (
              <Form.Item name="customer_id" label="所属客户">
                <Select
                  allowClear
                  showSearch
                  placeholder="选择客户"
                  style={{ width: 200 }}
                  loading={loadingCustomers}
                  optionFilterProp="children"
                  onChange={() => { saveStateToStorage({ page: 1 }); fetchList({ page: 1 }); }}
                  onSearch={() => triggerSearch(600)}
                >
                  {customers.map(c => (
                    <Select.Option key={c.id} value={c.id}>
                      {c.username}{c.customer_code ? ` (${c.customer_code})` : ''}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            <Form.Item name="project" label="所属项目">
              <Select
                allowClear
                mode="multiple"
                showSearch
                placeholder="请选择所属项目 可以多选"
                style={{ width: 240, minWidth: 200 }}
                loading={loadingProjects}
                optionFilterProp="label"
                onChange={() => { saveStateToStorage({ page: 1 }); fetchList({ page: 1 }); }}
                dropdownStyle={{ maxHeight: 300, minWidth: '400px' }}
                maxTagCount={1}
                maxTagTextLength={80}
                className="compact-multiselect"
              >
                {projects.map((p: any) => (
                  <Select.Option key={p.id} value={p.id.toString()} label={`${p.project_code} - ${p.name}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{p.project_code}</span>
                      <span className="text-gray-500 ml-2">{p.name}</span>
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="inventory_unit_code" label="所含库存单元">
              <Input
                allowClear
                placeholder="库存单元编码(精确)"
                onChange={handleInputChange('inventory_unit_code')}
                onPressEnter={() => fetchList({ page: 1 })}
                style={{ width: 200 }}
              />
            </Form.Item>
            <Form.Item name="sorting_code" label="所含分拣条码">
              <Input
                allowClear
                placeholder="分拣条码(精确)"
                onChange={handleInputChange('sorting_code')}
                onPressEnter={() => fetchList({ page: 1 })}
                style={{ width: 200 }}
              />
            </Form.Item>
            <Form.Item name="system_code" label="容器系统编码">
              <Input
                allowClear
                placeholder="支持模糊"
                onChange={handleInputChange('system_code')}
                onPressEnter={() => fetchList({ page: 1 })}
                style={{ width: 200 }}
              />
            </Form.Item>
            <Form.Item name="putaway_status" label="上架状态">
              <Select
                allowClear
                placeholder="不限"
                style={{ width: 140 }}
                onChange={() => fetchList({ page: 1 })}
                options={[
                  { value: 'off', label: '未上架' },
                  { value: 'on', label: '已上架' },
                ]}
              />
            </Form.Item>
          </div>

          {/* 第二行：内容物种类 仓库 当前仓位 类型 + 操作 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <Form.Item name="content_kind" label="内容物种类">
              <Select allowClear style={{ width: 140 }} onChange={() => { saveStateToStorage({ page: 1 }); fetchList({ page: 1 }); }} options={[
                { value: 'single', label: '单装' },
                { value: 'mixed', label: '混装' },
              ]} placeholder="不限" />
            </Form.Item>
            <Form.Item name="warehouse_code" label="仓库">
              <Select
                allowClear
                showSearch
                placeholder="选择仓库"
                style={{ width: 220 }}
                loading={loadingWarehouses}
                optionFilterProp="children"
                onChange={async (val) => {
                  saveStateToStorage({ page: 1 });
                  try {
                    const ls = await getLocations(val ? { warehouse_code: val } : undefined);
                    setLocations(ls || []);
                  } catch {}
                  fetchList({ page: 1 });
                }}
                onSearch={() => triggerSearch(600)}
              >
                {warehouses.map((w: any) => (
                  <Select.Option key={w.id} value={w.code}>
                    {w.name ? `${w.code} (${w.name})` : w.code}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="current_location_code" label="当前/最后仓位">
              <AutoComplete
                allowClear
                showSearch
                placeholder="选择固定仓位或输入临时仓位"
                style={{ width: 180 }}
                options={locations.map((loc) => ({ value: String(loc.code || '') }))}
                filterOption={(inputValue, option) => String(option?.value || '').toLowerCase().includes(String(inputValue || '').toLowerCase())}
                onChange={() => { saveStateToStorage({ page: 1 }); fetchList({ page: 1 }); }}
                onSelect={() => { saveStateToStorage({ page: 1 }); fetchList({ page: 1 }); }}
                onSearch={() => triggerSearch(600)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    saveStateToStorage({ page: 1 });
                    fetchList({ page: 1 });
                  }
                }}
              />
            </Form.Item>
            <Form.Item name="container_type" label="类型">
              <Select allowClear style={{ width: 140 }} onChange={() => { saveStateToStorage({ page: 1 }); fetchList({ page: 1 }); }} options={[
                { value: 'pallet', label: '托盘' },
                { value: 'box', label: '箱子' },
                { value: 'tote_bag', label: '袋子' },
                { value: 'direct', label: '直接存储' },
                { value: 'cart', label: '移动车' },
                { value: 'other', label: '其他' },
              ]} />
            </Form.Item>
            <Form.Item name="only_active_containers" valuePropName="checked" label="仅显示在用容器">
              <Switch
                onChange={(checked) => {
                  const kind = form.getFieldValue('container_kind');
                  form.setFieldsValue({
                    status: checked ? getInUseStatusByContainerKind(kind) : undefined,
                  });
                  saveStateToStorage({ page: 1 });
                  fetchList({ page: 1 });
                }}
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">搜索</Button>
                <Button onClick={() => { try { localStorage.removeItem(getStorageKey()); } catch {}; form.resetFields(); form.setFieldsValue({ only_active_containers: true }); fetchList({ page: 1 }); }}>重置</Button>
              </Space>
            </Form.Item>
          </div>
        </Form>
      </Card>

      {/* 仓储容器标签预览组件（与扫描页一致） */}
      <StorageLabelPreview
        visible={labelPreviewVisible}
        data={labelPreviewData}
        onClose={() => {
          setLabelPreviewVisible(false);
          setLabelPreviewData(undefined);
        }}
        onPrint={async (data) => {
          try {
            const printResult = await storageLabelService.printLabel(data);
            if ((printResult as any)?.success) {
              message.success(`标签打印成功！任务ID：${(printResult as any).printJobId || (printResult as any).print_job_id || ''}`);
            } else {
              message.error(`标签打印失败：${(printResult as any)?.message || ''}`);
            }
          } catch (error) {
            const errorInfo: any = handleLabelError ? handleLabelError(error, '打印') : { details: (error as any)?.message || '未知错误' };
            message.error(`标签打印失败：${errorInfo.details}`);
          }
        }}
        onDownload={async (data) => {
          try {
            const blob = await storageLabelService.downloadLabel(data);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `仓储容器标签_${(data as any)?.containerInfo?.systemCode || 'label'}_${new Date().toISOString().slice(0, 19).replace(/:/g, '')}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            message.success('标签下载成功！');
          } catch (error) {
            const errorInfo: any = handleLabelError ? handleLabelError(error, '下载') : { details: (error as any)?.message || '未知错误' };
            message.error(`标签下载失败：${errorInfo.details}`);
          }
        }}
      />
      <Modal
        title="IOC标签预览"
        open={outputLabelPreviewVisible}
        onCancel={() => {
          setOutputLabelPreviewVisible(false);
          if (outputLabelPreviewUrl) {
            try { URL.revokeObjectURL(outputLabelPreviewUrl); } catch {}
          }
          setOutputLabelPreviewUrl('');
        }}
        footer={null}
        width={920}
        destroyOnClose
      >
        {outputLabelPreviewUrl ? (
          <iframe
            src={outputLabelPreviewUrl}
            title="IOC标签预览"
            style={{ width: '100%', height: '72vh', border: 'none' }}
          />
        ) : (
          <div style={{ minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin tip="加载预览中..." />
          </div>
        )}
      </Modal>

      <Card>
        <Table
          loading={loading}
          dataSource={data}
          columns={columns}
          rowKey={(r: any) => `${r.container_kind || 'storage'}-${r.id}`}
          pagination={false}
          scroll={{ x: 1200 }}
          onChange={(pagination, filters, sorter: any) => {
            const field = sorter?.field as string | undefined;
            const order = sorter?.order as ('ascend' | 'descend' | undefined);
            if (field && (field === 'current_location_code' || field === 'created_at')) {
              const ord = order ? (order === 'descend' ? `-${field}` : field) : undefined;
              setSorterState({ field, order });
              saveStateToStorage({ page: 1, sorter: { field, order } });
              fetchList({ page: 1, ordering: ord });
              return;
            }
            // 非受控或清除排序
            setSorterState({});
            saveStateToStorage({ page: 1, sorter: {} });
            fetchList({ page: 1, ordering: undefined });
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            showQuickJumper
            onChange={(p, s) => { saveStateToStorage({ page: p, pageSize: s }); fetchList({ page: p, pageSize: s }); }}
            showTotal={(t, range) => `第 ${range[0]}-${range[1]} 条，共 ${t} 条记录`}
          />
        </div>
      </Card>

      <Card style={{ marginTop: 12 }} loading={detailLoading}>
        {!selectedContainer ? (
          <Empty description="选择上方容器查看概览（支持仓储容器与指令产出容器）" />
        ) : (
          <div>
            <div style={{ marginTop: 4, padding: 12, borderRadius: 8, border: '1px solid #d3adf7', background: '#f9f0ff' }}>
              <Title level={5}>容器概览</Title>
              <Space size={8} wrap>
                {(() => {
                  const code = String(selectedContainer?.container?.display_code || selectedContainer?.container?.system_code || '-').trim() || '-';
                  return (
                <Button
                  size="small"
                  type="primary"
                  style={{ background: '#1677ff', borderColor: '#1677ff', color: '#fff' }}
                  onClick={() => { void copyText(code, '容器编码已复制'); }}
                  title={code && code !== '-' ? '点击复制容器编码' : undefined}
                >
                  {code}
                </Button>
                  );
                })()}
                <Button
                  size="small"
                  style={{ background: '#722ed1', borderColor: '#722ed1', color: '#fff' }}
                >
                  {selectedContainer?.container?.container_kind === 'output' ? '指令产出容器' : '仓储容器'}
                </Button>
                {selectedContainer?.container?.container_kind === 'output' ? (
                  <Button
                    size="small"
                    style={{ background: '#531dab', borderColor: '#531dab', color: '#fff' }}
                  >
                    {String(selectedContainer?.container?.content_kind || '').toLowerCase() === 'exception'
                      ? '异常内容容器'
                      : '正常内容容器'}
                  </Button>
                ) : null}
                <Button
                  size="small"
                  style={{ background: '#13c2c2', borderColor: '#13c2c2', color: '#fff' }}
                >
                  {selectedContainer?.container?.container_type_display || '-'}
                </Button>
                <Button
                  size="small"
                  style={{
                    background: (
                      String(selectedContainer?.container?.status || '').toLowerCase() === 'active'
                      || isOutputInUseStatus(
                        selectedContainer?.container?.status,
                        selectedContainer?.container?.container_kind,
                      )
                    ) ? '#52c41a' : '#595959',
                    borderColor: (
                      String(selectedContainer?.container?.status || '').toLowerCase() === 'active'
                      || isOutputInUseStatus(
                        selectedContainer?.container?.status,
                        selectedContainer?.container?.container_kind,
                      )
                    ) ? '#52c41a' : '#595959',
                    color: '#fff',
                  }}
                >
                  {mapStatusDisplayText(
                    selectedContainer?.container?.status,
                    selectedContainer?.container?.status_display,
                    selectedContainer?.container?.container_kind,
                  )}
                </Button>
                <Button type="primary" danger size="small">
                  {`${Number(selectedContainer?.contents?.kinds || 0)}款${Number(selectedContainer?.contents?.total_qty || 0)}件`}
                </Button>
                {selectedContainer?.container?.container_kind === 'output' ? (
                  <span style={{ margin: '0 10px', color: '#8c8c8c', fontWeight: 700 }}>|</span>
                ) : null}
                <Button
                  size="small"
                  style={{ background: '#8c8c8c', borderColor: '#8c8c8c', color: '#fff' }}
                >
                  {`当前/最后仓位 ${selectedContainer?.container?.current_location_display || selectedContainer?.container?.current_location_code || '-'}`}
                </Button>
                {selectedContainer?.container?.container_kind === 'output' ? (
                  <span style={{ margin: '0 10px', color: '#8c8c8c', fontWeight: 700 }}>|</span>
                ) : null}
                {selectedContainer?.container?.container_kind === 'output' ? (
                  <Button
                    size="small"
                    style={{
                      background: String(selectedContainer?.container?.latest_reservation_status || '').trim().toLowerCase() === 'active' ? '#ff7875' : '#52c41a',
                      borderColor: String(selectedContainer?.container?.latest_reservation_status || '').trim().toLowerCase() === 'active' ? '#ff7875' : '#52c41a',
                      color: '#fff',
                    }}
                  >
                    {`最新占用状态 ${String(selectedContainer?.container?.latest_reservation_status || '').trim().toLowerCase() === 'active' ? '已占用' : '未占用'}`}
                  </Button>
                ) : null}
                {selectedContainer?.container?.container_kind === 'output' ? (
                  <Button
                    size="small"
                    style={{
                      background: '#2f54eb',
                      borderColor: '#2f54eb',
                      color: '#fff',
                    }}
                  >
                    {'被占用指令 '}
                    {(Number(selectedContainer?.container?.occupying_instruction_id || 0) > 0
                      && String(selectedContainer?.container?.occupying_instruction_code || '').trim()) ? (
                        <span
                          role="button"
                          tabIndex={0}
                          style={{ color: '#fff', textDecoration: 'underline', cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            try {
                              const instId = Number(selectedContainer?.container?.occupying_instruction_id || 0);
                              if (!Number.isFinite(instId) || instId <= 0) return;
                              const w: any = window as any;
                              const path = String(w?.location?.pathname || '');
                              const m = path.match(/^\/dashboard\/(admin|staff|customer)(?:\/|$)/);
                              const role = m?.[1] || 'customer';
                              const detailUrl = `/dashboard/${role}/instructions/i-${instId}`;
                              if (w.tabsManager && typeof w.tabsManager.openTabByPath === 'function') {
                                w.tabsManager.openTabByPath(detailUrl, { forceNew: true });
                                return;
                              }
                              w.open(detailUrl, '_blank');
                            } catch {}
                          }}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter' && e.key !== ' ') return;
                            e.preventDefault();
                            e.stopPropagation();
                            try {
                              const instId = Number(selectedContainer?.container?.occupying_instruction_id || 0);
                              if (!Number.isFinite(instId) || instId <= 0) return;
                              const w: any = window as any;
                              const path = String(w?.location?.pathname || '');
                              const m = path.match(/^\/dashboard\/(admin|staff|customer)(?:\/|$)/);
                              const role = m?.[1] || 'customer';
                              const detailUrl = `/dashboard/${role}/instructions/i-${instId}`;
                              if (w.tabsManager && typeof w.tabsManager.openTabByPath === 'function') {
                                w.tabsManager.openTabByPath(detailUrl, { forceNew: true });
                                return;
                              }
                              w.open(detailUrl, '_blank');
                            } catch {}
                          }}
                        >
                          {String(selectedContainer?.container?.occupying_instruction_code || '').trim()}
                        </span>
                      ) : (
                        '-'
                      )}
                  </Button>
                ) : null}
              </Space>
            </div>
            {String(selectedContainer?.container?.container_kind || '').trim().toLowerCase() === 'output' ? (
              <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: '1px solid #ffd591', background: '#fff7e6' }}>
                <Title level={5}>容器层级</Title>
                {(() => {
                  const hierarchy = (selectedContainer?.container?.hierarchy || {}) as any;
                  const role = String(hierarchy?.role || 'standalone').trim().toLowerCase();
                  const parent = hierarchy?.parent_container || null;
                  const children = Array.isArray(hierarchy?.child_containers) ? hierarchy.child_containers : [];
                  const renderHierarchyTag = (roleRaw: any) => {
                    const rv = String(roleRaw || '').trim().toLowerCase();
                    if (rv === 'child') return <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#fff1f0', borderColor: '#ffa39e', color: '#cf1322', fontWeight: 700 }}>子</Tag>;
                    if (rv === 'parent') return <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9', fontWeight: 700 }}>父</Tag>;
                    return <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontWeight: 700 }}>独立</Tag>;
                  };
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: '#595959' }}>当前容器层级：</span>
                        {renderHierarchyTag(role)}
                      </div>
                      {parent ? (
                        <div>
                          <div style={{ color: '#595959', marginBottom: 6 }}>所属父容器</div>
                          <Space size={8} wrap>
                            <Button
                              size="small"
                              style={{ background: '#1677ff', borderColor: '#1677ff', color: '#fff' }}
                              onClick={() => {
                                const pid = Number(parent?.id || 0);
                                if (!(pid > 0)) return;
                                void loadContainerDetail(
                                  { id: pid, kind: 'output' },
                                  { cPage: 1, cPageSize: 20, hPage: 1, hPageSize: 20, mPage: 1, mPageSize: 20 },
                                );
                              }}
                            >
                              {String(parent?.code || '-')}
                            </Button>
                            <Button
                              size="small"
                              type="text"
                              icon={<CopyOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                void copyText(String(parent?.code || '').trim(), '父容器编码已复制');
                              }}
                            />
                            <Tag>{String(parent?.status_display || '-')}</Tag>
                            <Tag>{String(parent?.current_location_display || '-')}</Tag>
                          </Space>
                        </div>
                      ) : null}
                      {children.length > 0 ? (
                        <div>
                          <div style={{ color: '#595959', marginBottom: 6 }}>子容器列表</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {children.map((ch: any, idx: number) => (
                              <Space key={`hier-child-${String(ch?.id || idx)}`} size={6} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #ffe7ba', background: '#fff' }}>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    const cid = Number(ch?.id || 0);
                                    if (!(cid > 0)) return;
                                    void loadContainerDetail(
                                      { id: cid, kind: 'output' },
                                      { cPage: 1, cPageSize: 20, hPage: 1, hPageSize: 20, mPage: 1, mPageSize: 20 },
                                    );
                                  }}
                                >
                                  {String(ch?.code || '-')}
                                </Button>
                                <Button
                                  size="small"
                                  type="text"
                                  icon={<CopyOutlined />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void copyText(String(ch?.code || '').trim(), '子容器编码已复制');
                                  }}
                                />
                                <Tag>{String(ch?.status_display || '-')}</Tag>
                              </Space>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      {role === 'standalone' && !parent && children.length === 0 ? (
                        <div style={{ color: '#8c8c8c' }}>该容器暂无父子层级关系。</div>
                      ) : null}
                    </div>
                  );
                })()}
              </div>
            ) : null}
            <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: '1px solid #91caff', background: '#f0f9ff' }}>
              <Title level={5}>容器当前内容</Title>
              {(() => {
                const isOutput = String(selectedContainer?.container?.container_kind || '').trim().toLowerCase() === 'output';
                const isNormalOutput = String(selectedContainer?.container?.content_kind || '').trim().toLowerCase() === 'normal';
                const isRelabelSource = Boolean(selectedContainer?.container?.is_relabel_pack_instruction);
                const relabelSummary = String(selectedContainer?.container?.relabel_new_barcode_summary || '').trim();
                if (!(isOutput && isNormalOutput && (isRelabelSource || relabelSummary))) return null;
                const merged = relabelSummary;
                if (!merged || merged === '-') return null;
                const rawLines = merged
                  .split(/[；;\n,]+/)
                  .map((x: string) => String(x || '').trim())
                  .filter(Boolean);
                if (!rawLines.length) return null;
                const parsedItems = rawLines
                  .map((line: string) => {
                    const m = line.match(/^(.*?)\s*[xX×]\s*(\d+)\s*件?$/);
                    const barcode = String(m?.[1] || line).trim();
                    const qty = Number(m?.[2] || 0);
                    return { barcode, qty, text: qty > 0 ? `${barcode} × ${qty}件` : barcode };
                  })
                  .filter((it) => !!it.barcode);
                if (!parsedItems.length) return null;
                const dedupMap = new Map<string, { barcode: string; qty: number; text: string }>();
                parsedItems.forEach((it) => {
                  if (!dedupMap.has(it.barcode)) {
                    dedupMap.set(it.barcode, { ...it });
                    return;
                  }
                  const prev = dedupMap.get(it.barcode)!;
                  const sumQty = Number(prev.qty || 0) + Number(it.qty || 0);
                  dedupMap.set(it.barcode, {
                    barcode: it.barcode,
                    qty: sumQty,
                    text: sumQty > 0 ? `${it.barcode} × ${sumQty}件` : it.barcode,
                  });
                });
                const uniqueItems = Array.from(dedupMap.values());
                const preview = uniqueItems.slice(0, 3);
                const hasMore = uniqueItems.length > 3;
                return (
                  <div style={{ marginBottom: 10, padding: 10, borderRadius: 6, border: '1px dashed #91caff', background: '#ffffff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, color: '#262626', fontWeight: 600 }}>
                      <span>新条码(换标后)</span>
                      <Tooltip title="此IOC来源于换标指令,此处显示换标后的新条码.">
                        <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
                      </Tooltip>
                    </div>
                    <div>
                      {preview.map((line, idx: number) => (
                        <Tooltip title={line.text} key={`${line.barcode}-${idx}`}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {line.text}
                          </div>
                        </Tooltip>
                      ))}
                      {hasMore ? (
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, height: 'auto', lineHeight: 1.2 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const containerCode = String(
                              selectedContainer?.container?.display_code
                              || selectedContainer?.container?.system_code
                              || '-'
                            ).trim() || '-';
                            const fullInfoText = [
                              `容器系统编码: ${containerCode}`,
                              ...uniqueItems.map((x, i) => `${i + 1}. ${x.text}`),
                            ].join('\n');
                            Modal.info({
                              title: (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 12, paddingRight: 24 }}>
                                  <span style={{ justifySelf: 'start' }}>新条码(换标后)完整记录</span>
                                  <span style={{ justifySelf: 'center', color: '#1677ff', fontWeight: 700, fontFamily: 'monospace' }}>{containerCode}</span>
                                  <span style={{ justifySelf: 'end' }}>
                                    <Button
                                      type="link"
                                      size="small"
                                      onClick={async () => {
                                        void copyText(fullInfoText, '已复制完整信息');
                                      }}
                                    >
                                      复制信息
                                    </Button>
                                  </span>
                                </div>
                              ),
                              width: 720,
                              centered: true,
                              content: (
                                <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                                  {uniqueItems.map((x, i: number) => (
                                    <div key={`${x.barcode}-${i}`} style={{ marginBottom: 6, wordBreak: 'break-all' }}>
                                      {i + 1}. {x.text}
                                    </div>
                                  ))}
                                </div>
                              ),
                            });
                          }}
                        >
                          [...]
                        </Button>
                      ) : null}
                    </div>
                  </div>
                );
              })()}
              <Table
                dataSource={selectedContainer.contents?.results || []}
                rowKey="id"
                size="small"
                pagination={{
                  current: Number(selectedContainer?.contents?.page || detailPager.cPage),
                  pageSize: Number(selectedContainer?.contents?.page_size || detailPager.cPageSize),
                  total: Number(selectedContainer?.contents?.count || 0),
                  showSizeChanger: true,
                  showTotal: (t, range) => `第 ${range[0]}-${range[1]} 条，共 ${t} 条记录`,
                  onChange: (p, s) => {
                    if (!selectedContainerRef) return;
                    loadContainerDetail(selectedContainerRef, { cPage: p, cPageSize: s });
                  },
                }}
                columns={(() => {
                  const isOutput = String(selectedContainer?.container?.container_kind || '').trim().toLowerCase() === 'output';
                  const isExceptionOutput = String(selectedContainer?.container?.content_kind || '').trim().toLowerCase() === 'exception';
                  const baseCols: any[] = [
                    {
                      title: '库存单元系统编码',
                      dataIndex: 'inventory_unit_code',
                      key: 'inventory_unit_code',
                      width: 180,
                      render: (v: string) => {
                        const code = String(v || '').trim();
                        return (
                          <Space size={4}>
                            <Text code>{code || '-'}</Text>
                            {code ? (
                              <Button
                                type="text"
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void copyText(code, '库存单元系统编码已复制');
                                }}
                                style={{ padding: 0, width: 20, minWidth: 20, height: 20, color: '#1677ff' }}
                              />
                            ) : null}
                          </Space>
                        );
                      },
                    },
                    {
                      title: '库存单元内容物',
                      key: 'inventory_unit_content',
                      width: 300,
                      render: (_: any, row: any) => (
                        row?.inventory_unit_content
                        || row?.inventory_unit_content_display
                        || row?.content_summary
                        || row?.content_items
                        || '-'
                      ),
                    },
                    { title: '类型', dataIndex: 'inventory_unit_type_display', key: 'inventory_unit_type_display', width: 130 },
                  ];
                  if (isOutput && isExceptionOutput) {
                    baseCols.push({
                      title: '补充说明',
                      key: 'extra_note',
                      width: 240,
                      render: () => '无法操作等异常产品',
                    });
                  }
                  baseCols.push(
                    { title: '数量', dataIndex: 'quantity', key: 'quantity', width: 110 },
                    { title: '备注', dataIndex: 'remarks', key: 'remarks' },
                  );
                  return baseCols;
                })()}
              />
            </div>
            <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: '1px solid #b7eb8f', background: '#f6ffed' }}>
              <Title level={5}>内容变动历史</Title>
              <Table
                dataSource={selectedContainer.content_history?.results || []}
                rowKey="id"
                size="small"
                pagination={{
                  current: Number(selectedContainer?.content_history?.page || detailPager.hPage),
                  pageSize: Number(selectedContainer?.content_history?.page_size || detailPager.hPageSize),
                  total: Number(selectedContainer?.content_history?.count || 0),
                  showSizeChanger: true,
                  showTotal: (t, range) => `第 ${range[0]}-${range[1]} 条，共 ${t} 条记录`,
                  onChange: (p, s) => {
                    if (!selectedContainerRef) return;
                    loadContainerDetail(selectedContainerRef, { hPage: p, hPageSize: s });
                  },
                }}
                columns={[
                  { title: '时间', dataIndex: 'created_at', key: 'created_at', render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm') },
                  { title: '库存单元', dataIndex: 'inventory_unit_code', key: 'inventory_unit_code', render: (v: string) => <Text code>{v}</Text> },
                  { title: '原因', dataIndex: 'change_reason_display', key: 'change_reason_display' },
                  { title: 'Δ数量', dataIndex: 'delta_quantity', key: 'delta_quantity' },
                  { title: '变动后', dataIndex: 'quantity_after', key: 'quantity_after' },
                  { title: '操作人', dataIndex: 'actor_name', key: 'actor_name' },
                  {
                    title: '相关业务单号',
                    dataIndex: 'reference_number',
                    key: 'reference_number',
                    render: (v: any) => {
                      const ref = String(v || '').trim();
                      return (
                        <Space size={4}>
                          <Text code>{ref || '-'}</Text>
                          {ref ? (
                            <Button
                              type="text"
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                void copyText(ref, '相关业务单号已复制');
                              }}
                              style={{ padding: 0, width: 20, minWidth: 20, height: 20, color: '#1677ff' }}
                            />
                          ) : null}
                        </Space>
                      );
                    },
                  },
                  { title: '备注', dataIndex: 'remarks', key: 'remarks' },
                ]}
              />
            </div>
            <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: '1px solid #d9d9d9', background: '#fafafa' }}>
              <Title level={5}>移动记录（上架/下架/移位）</Title>
              <Table
                dataSource={selectedContainer.movement_logs?.results || []}
                rowKey="id"
                size="small"
                pagination={{
                  current: Number(selectedContainer?.movement_logs?.page || detailPager.mPage),
                  pageSize: Number(selectedContainer?.movement_logs?.page_size || detailPager.mPageSize),
                  total: Number(selectedContainer?.movement_logs?.count || 0),
                  showSizeChanger: true,
                  showTotal: (t, range) => `第 ${range[0]}-${range[1]} 条，共 ${t} 条记录`,
                  onChange: (p, s) => {
                    if (!selectedContainerRef) return;
                    loadContainerDetail(selectedContainerRef, { mPage: p, mPageSize: s });
                  },
                }}
                columns={[
                  { title: '时间', dataIndex: 'moved_at', key: 'moved_at', render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm') },
                  { title: '来源', dataIndex: 'from_location_code', key: 'from_location_code' },
                  { title: '目标', dataIndex: 'to_location_code', key: 'to_location_code' },
                  { title: '操作人', dataIndex: 'operator_name', key: 'operator_name' },
                  { title: '备注', dataIndex: 'remarks', key: 'remarks' },
                  { title: '关联单号', dataIndex: 'reference_number', key: 'reference_number' },
                ]}
              />
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default InventoryLocationPlaceholder;


